import React from "react";
import "./AttendanceTable.css";
import { AddAttendanceModal } from "./AddAttendanceModal";
import { extractTime } from "../utils/dateTime";

/**
 * Formate une heure ISO en HH:MM EN HEURE FRANÇAISE (Europe/Paris)
 * @param {string} isoTime - Time ISO (ex: "2025-09-29T08:30:00.000Z")
 * @returns {string} Heure formatée en heure française (ex: "10:30")
 */
function formatTime(isoTime) {
  if (!isoTime) return "--:--";
  // ✅ FORCE timezone Europe/Paris pour affichage
  return extractTime(isoTime) || "--:--";
}

/**
 * Formate une durée en minutes vers HH:MM
 * @param {number} minutes - Durée en minutes
 * @returns {string} Durée formatée (ex: "01:30")
 */
function formatDuration(minutes) {
  const mins = Number(minutes) || 0;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainingMins).padStart(2, '0')}`;
}

/**
 * Formate une durée en minutes vers HHh MM (pour le total)
 * @param {number} minutes - Durée en minutes
 * @returns {string} Durée formatée (ex: "01H 30")
 */
function formatTotal(minutes) {
  const mins = Number(minutes) || 0;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${String(hours).padStart(2, '0')}H ${String(remainingMins).padStart(2, '0')}`;
}

/**
 * Tableau d'attendance avec édition inline
 * @param {{data: Array, onUpdate: Function, onAdd: Function, allOperators: Array}} props
 */
export function AttendanceTable({ data, onUpdate, onAdd, allOperators = [] }) {
  const [editingCell, setEditingCell] = React.useState(null);
  const [editValue, setEditValue] = React.useState("");
  const [selectedOperator, setSelectedOperator] = React.useState("all");
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 30;
  
  const [localData, setLocalData] = React.useState([]);
  
  // ✅ Synchroniser localData avec data SAUF si on est en train d'éditer
  React.useEffect(() => {
    // Si on est en train d'éditer, NE PAS synchroniser (pour garder la saisie)
    if (editingCell) {
      console.log('✏️ [AttendanceTable] Édition en cours, pas de sync');
      return;
    }
    
    // Sinon, mettre à jour localData avec les nouvelles data
    console.log('🔄 [AttendanceTable] Sync data →', data?.length, 'lignes');
    setLocalData(data || []);
  }, [data, editingCell]);

  // Extraire la liste unique des opérateurs
  const operators = React.useMemo(() => {
    if (!localData) return [];
    const uniqueOps = new Map();
    localData.forEach(row => {
      if (row.operator_id && !uniqueOps.has(row.operator_id)) {
        uniqueOps.set(row.operator_id, row.operator_name);
      }
    });
    return Array.from(uniqueOps.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [localData]);

  // Filtrer les données selon l'opérateur sélectionné
  const filteredData = React.useMemo(() => {
    if (!localData) return [];
    if (selectedOperator === "all") return localData;
    return localData.filter(row => String(row.operator_id) === String(selectedOperator));
  }, [localData, selectedOperator]);

  // Calculer la pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Reset page quand on change de filtre
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedOperator]);

  const handleEdit = (rowId, field, currentValue) => {
    setEditingCell({ rowId, field });
    setEditValue(currentValue || "");
  };

  const handleSave = async (row) => {
    if (!editingCell) return;
    const { field } = editingCell;

    const timePattern = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/;
    const durationPattern = /^([0-9]{1,2}):([0-5][0-9])$/;
    const isTimeField = field === 'check_in' || field === 'check_out';
    const isBreak = field === 'break_minutes';

    if (isTimeField && !timePattern.test(editValue)) {
      alert('Format invalide. Utilisez HH:MM (ex: 08:30)');
      return;
    }
    if (isBreak && !durationPattern.test(editValue)) {
      alert('Format invalide pour la pause. Utilisez HH:MM');
      return;
    }

    console.log('🟢 [handleSave] Valeur saisie:', editValue);
    console.log('🟢 [handleSave] Ligne avant MAJ:', row);

    // ✅ ENVOYER AU BACKEND D'ABORD
    if (onUpdate) {
      console.log('🟢 [handleSave] Envoi au backend...');
      const updatedRow = await onUpdate(row.id, field, editValue);
      console.log('🟢 [handleSave] Backend répond:', updatedRow);
      
      // ✅ Si backend OK, mettre à jour localData
      if (updatedRow) {
        setLocalData(prev => {
          const updated = prev.map(r => {
            if (r.id !== row.id) return r;
            
            // Créer une copie avec la nouvelle valeur
            const newRow = { ...r };
            
            if (isTimeField) {
              // Convertir HH:MM en timestamp ISO
              const [hours, minutes] = editValue.split(':').map(Number);
              const date = new Date(r.local_date);
              date.setHours(hours, minutes, 0, 0);
              newRow[field] = date.toISOString();
            } else if (isBreak) {
              const [h, m] = editValue.split(':').map(Number);
              newRow.break_minutes = h * 60 + m;
            }
            
            console.log('🟢 [handleSave] Ligne APRÈS MAJ:', newRow);
            return newRow;
          });
          return updated;
        });
      } else {
        // ✅ Si backend échoue, ne pas fermer l'édition - garder la valeur
        console.error('❌ [handleSave] Backend a échoué, valeur conservée');
        return; // Ne pas fermer le mode édition
      }
    }

    // ✅ Fermer le mode édition SEULEMENT si backend OK
    setEditingCell(null);
    setEditValue("");
  };

  const handleCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  if (!localData || localData.length === 0) {
    return (
      <div className="attendance-table-empty">
        <span className="empty-icon">📋</span>
        <p>Aucune donnée de présence sur cette période</p>
      </div>
    );
  }

  const handleAddSubmit = async (formData) => {
    const newRow = await onAdd(formData);
    if (newRow) {
      // Insérer la nouvelle ligne à la bonne position selon la date (plus récente en premier)
      setLocalData(prev => {
        const updated = [...prev, newRow];
        // Trier par date décroissante
        return updated.sort((a, b) => {
          const dateA = new Date(a.local_date);
          const dateB = new Date(b.local_date);
          return dateB - dateA; // Plus récent en premier
        });
      });
    }
    setIsModalOpen(false);
  };

  return (
    <div className="attendance-table-container">
      <div className="table-header-section">
        <div className="table-header-left">
          <h4>Détails des présences</h4>
          <select 
            className="operator-filter"
            value={selectedOperator}
            onChange={(e) => setSelectedOperator(e.target.value)}
          >
            <option value="all">Tout le monde</option>
            {operators.map(op => (
              <option key={op.id} value={op.id}>{op.name}</option>
            ))}
          </select>
        </div>
        <button className="btn-add-attendance" onClick={() => setIsModalOpen(true)}>
          + Ajouter une présence
        </button>
      </div>

      <AddAttendanceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        operators={allOperators}
        onSubmit={handleAddSubmit}
      />
      
      <div className="attendance-table-wrapper">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Opérateur</th>
              <th>Arrivée</th>
              <th>Départ</th>
              <th>Pause</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row) => (
              <tr key={row.id} className="table-row">
                <td className="date-cell">
                  <span className="date-text">
                    {new Date(row.local_date).toLocaleDateString('fr-FR', { 
                      day: '2-digit', 
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </td>
                
                <td className="operator-cell">
                  <div className="operator-info">
                    <div className="operator-avatar">
                      {row.operator_name?.charAt(0) || '?'}
                    </div>
                    <span className="operator-name">{row.operator_name || 'Inconnu'}</span>
                  </div>
                </td>

                <td className={`time-cell ${editingCell?.rowId === row.id && editingCell?.field === 'check_in' ? 'editing' : ''}`}>
                  {editingCell?.rowId === row.id && editingCell?.field === 'check_in' ? (
                    <div className="edit-cell-container">
                      <input
                        type="time"
                        className="time-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSave(row);
                          if (e.key === 'Escape') handleCancel();
                        }}
                        autoFocus
                      />
                      <div className="edit-actions">
                        <button 
                          className="edit-btn validate-btn"
                          onClick={() => handleSave(row)}
                          title="Valider"
                        >
                          ✓
                        </button>
                        <button 
                          className="edit-btn cancel-btn"
                          onClick={handleCancel}
                          title="Annuler"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="time-display"
                      onClick={() => handleEdit(row.id, 'check_in', formatTime(row.check_in))}
                    >
                      <span className="time-icon">🕐</span>
                      {formatTime(row.check_in)}
                    </div>
                  )}
                </td>

                <td className={`time-cell ${editingCell?.rowId === row.id && editingCell?.field === 'check_out' ? 'editing' : ''}`}>
                  {editingCell?.rowId === row.id && editingCell?.field === 'check_out' ? (
                    <div className="edit-cell-container">
                      <input
                        type="time"
                        className="time-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSave(row);
                          if (e.key === 'Escape') handleCancel();
                        }}
                        autoFocus
                      />
                      <div className="edit-actions">
                        <button 
                          className="edit-btn validate-btn"
                          onClick={() => handleSave(row)}
                          title="Valider"
                        >
                          ✓
                        </button>
                        <button 
                          className="edit-btn cancel-btn"
                          onClick={handleCancel}
                          title="Annuler"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="time-display"
                      onClick={() => handleEdit(row.id, 'check_out', formatTime(row.check_out))}
                    >
                      <span className="time-icon">🕐</span>
                      {formatTime(row.check_out)}
                    </div>
                  )}
                </td>

                <td className={`duration-cell ${editingCell?.rowId === row.id && editingCell?.field === 'break_minutes' ? 'editing' : ''}`}>
                  {editingCell?.rowId === row.id && editingCell?.field === 'break_minutes' ? (
                    <div className="edit-cell-container">
                      <input
                        type="text"
                        className="time-input"
                        placeholder="HH:MM"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSave(row);
                          if (e.key === 'Escape') handleCancel();
                        }}
                        autoFocus
                      />
                      <div className="edit-actions">
                        <button 
                          className="edit-btn validate-btn"
                          onClick={() => handleSave(row)}
                          title="Valider"
                        >
                          ✓
                        </button>
                        <button 
                          className="edit-btn cancel-btn"
                          onClick={handleCancel}
                          title="Annuler"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="duration-display"
                      onClick={() => handleEdit(row.id, 'break_minutes', formatDuration(row.break_minutes))}
                    >
                      <span className="pause-icon">☕</span>
                      {formatDuration(row.break_minutes)}
                    </div>
                  )}
                </td>

                <td className="total-cell">
                  <span className="total-badge">
                    {formatTotal(row.work_minutes)}
                  </span>
                </td>

                <td className="actions-cell">
                  <button 
                    className="action-btn delete-btn" 
                    title="Supprimer"
                    onClick={() => onUpdate && onUpdate(row.id, 'delete', null)}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Affichage {startIndex + 1}-{Math.min(endIndex, filteredData.length)} sur {filteredData.length} entrées
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ← Précédent
            </button>
            <span className="pagination-page">
              Page {currentPage} sur {totalPages}
            </span>
            <button 
              className="pagination-btn" 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Suivant →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
